import { FocusMonitor, ActiveDescendantKeyManager, A11yModule } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i6 from '@angular/cdk/overlay';
import { CdkConnectedOverlay, OverlayModule } from '@angular/cdk/overlay';
import * as i0 from '@angular/core';
import { Injectable, inject, ElementRef, Directive, Input, InjectionToken, Component, ChangeDetectionStrategy, EventEmitter, DestroyRef, booleanAttribute, numberAttribute, Optional, Self, Inject, Output, ViewChild, ViewChildren, NgModule } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as i1 from '@angular/forms';
import { NgControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as i8 from '@aposin/ng-aquila/formfield';
import { NxFormfieldControl, FORMFIELD_DEFAULT_OPTIONS, NxFormfieldModule } from '@aposin/ng-aquila/formfield';
import * as i2 from '@aposin/ng-aquila/utils';
import { pad } from '@aposin/ng-aquila/utils';
import { Subject } from 'rxjs';
import * as i4 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i5 from '@aposin/ng-aquila/icon';
import { NxIconModule } from '@aposin/ng-aquila/icon';
import * as i7 from '@aposin/ng-aquila/radio-toggle';
import { NxRadioToggleModule } from '@aposin/ng-aquila/radio-toggle';
import { NxButtonModule } from '@aposin/ng-aquila/button';
import { NxInputModule } from '@aposin/ng-aquila/input';
import { NxRadioModule } from '@aposin/ng-aquila/radio-button';

class NxTimefieldIntl {
    constructor() {
        /**
         * Stream that emits whenever the labels here are changed. Use this to notify
         * components if the labels have changed after initialization.
         */
        this.changes = new Subject();
        /** Label that should replace the 'hours' aria-label of the input field. */
        this.inputFieldHoursAriaLabel = 'hours';
        /** Label that should replace the 'minutes' input field of the aria-label. */
        this.inputFieldMinutesAriaLabel = 'minutes';
        /** Label for the time picker button. */
        this.buttonOpenTimepickerAriaLabel = 'Open time picker';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: NxTimefieldIntl, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: NxTimefieldIntl }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: NxTimefieldIntl, decorators: [{
            type: Injectable
        }] });

/** The next id to use for creating unique DOM IDs. */
let nextId$1 = 0;
class NxTimefieldOption {
    /** The id of the option's host element. */
    get id() {
        return this._id || this._generatedId;
    }
    set id(value) {
        this._id = value;
    }
    constructor(_cdr) {
        this._cdr = _cdr;
        this._active = false;
        this.value = '';
        this.selected = false;
        this._id = '';
        this._generatedId = `nx-timefield-option-${nextId$1++}`;
        /** The option's host element */
        this.element = inject(ElementRef).nativeElement;
    }
    setActiveStyles() {
        this._active = true;
        this._cdr.markForCheck();
    }
    setInactiveStyles() {
        this._active = false;
        this._cdr.markForCheck();
    }
    getLabel() {
        return this.element.textContent?.trim() ?? '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: NxTimefieldOption, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.2", type: NxTimefieldOption, isStandalone: true, selector: "[nxTimefieldOption]", inputs: { value: ["nxTimefieldOption", "value"], selected: "selected", id: "id" }, host: { attributes: { "role": "option" }, properties: { "id": "id", "attr.aria-selected": "selected", "class.is-active": "_active" }, classAttribute: "nx-timefield-option" }, exportAs: ["nxTimefieldOption"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: NxTimefieldOption, decorators: [{
            type: Directive,
            args: [{
                    selector: '[nxTimefieldOption]',
                    standalone: true,
                    exportAs: 'nxTimefieldOption',
                    host: {
                        role: 'option',
                        class: 'nx-timefield-option',
                        '[id]': 'id',
                        '[attr.aria-selected]': 'selected',
                        '[class.is-active]': '_active',
                    },
                }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { value: [{
                type: Input,
                args: [{ alias: 'nxTimefieldOption' }]
            }], selected: [{
                type: Input
            }], id: [{
                type: Input
            }] } });

/**
 * Generate time array from start time to end time with a specified interval (in minutes)
 *
 * Example: Generate time array from 08:00 to 17:30 with a 30-minute difference
 * const timeArray = generateTimeArray("08:00", "17:30", 30);
 */
function getTimeArray(startTime, endTime, interval, twelveHourFormat = false) {
    const isoTimeRegex = /^\d{2}:\d{2}$/;
    if (!isoTimeRegex.test(startTime) || !isoTimeRegex.test(endTime)) {
        throw new Error('Invalid ISO time format. Use HH:mm format.');
    }
    // Convert start and end time to Date objects
    const startDate = new Date(`2000-01-01T${startTime}`);
    const endDate = new Date(`2000-01-01T${endTime}`);
    if (startDate >= endDate) {
        throw new Error('startTime must be less than endTime.');
    }
    const result = [];
    // Loop through the time range with the specified minute span
    while (startDate < endDate) {
        // Get hours and minutes
        let hours = startDate.getHours();
        const minutes = startDate.getMinutes().toString().padStart(2, '0');
        const value = `${hours.toString().padStart(2, '0')}:${minutes}`;
        let label = value;
        // Convert to 12-hour format if specified
        if (twelveHourFormat) {
            const period = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12 || 12; // Convert 0 to 12
            label = `${hours}:${minutes} ${period}`;
        }
        result.push({ value, label });
        // Increment the time by the specified minute span
        startDate.setMinutes(startDate.getMinutes() + interval);
    }
    return result;
}
/**
 * Find closest time in the array to the target time
 *
 * // Example usage:
 * const timeArray = ["08:00", "12:30", "15:45", "18:00"];
 * const targetTime = "13:15";
 *
 * const closestTime = findClosestTime(timeArray, targetTime);
 *
 * console.log(`The closest time to ${targetTime} is ${closestTime}`);
 */
function getClosestTime(timeArray, targetTime) {
    if (!timeArray?.length) {
        return '';
    }
    // Convert target time to Date object
    const targetDate = new Date(`2000-01-01T${targetTime}`);
    // Convert all times in the array to Date objects
    const dateArray = timeArray.map(time => new Date(`2000-01-01T${time}`));
    // Find the closest date in the array to the target date
    const closestDate = dateArray.reduce((closest, current) => {
        const closestDiff = Math.abs(targetDate.getTime() - closest?.getTime());
        const currentDiff = Math.abs(targetDate.getTime() - current?.getTime());
        return currentDiff < closestDiff ? current : closest;
    });
    // Convert the closest date back to "hh:mm" format
    const closestTime = `${closestDate.getHours().toString().padStart(2, '0')}:${closestDate.getMinutes().toString().padStart(2, '0')}`;
    return closestTime;
}

/* eslint-disable @angular-eslint/no-conflicting-lifecycle */
let nextId = 0;
const DEFAULT_END_TIME = '24:00';
const DEFAULT_START_TIME = '00:00';
const DEFAULT_TIME_SPAN = 30;
function TIMEFIELD_DEFAULT_OPTIONS_FACTORY() {
    return {
        withTimepicker: false,
    };
}
const TIMEFIELD_DEFAULT_OPTIONS = new InjectionToken('FORMFIELD_DEFAULT_OPTIONS', {
    providedIn: 'root',
    factory: TIMEFIELD_DEFAULT_OPTIONS_FACTORY,
});
class TimefieldDefaultOptions {
    constructor() {
        this.withTimepicker = false;
    }
}
class NxTimefieldControl {
    constructor() {
        this.timefield = inject(NxTimefieldComponent);
        this.ngControl = inject(NgControl, { optional: true, self: true });
        this.value = null;
        this.stateChanges = new Subject();
        this.id = ''; // noop for now
        this.focused = false;
        this.required = false;
        this.disabled = false;
        this.readonly = false;
        this.shouldLabelFloat = true;
        this.errorState = false;
        this.placeholder = '';
        this.controlType = 'nx-timefield';
        this.updateOn = 'change';
    }
    get empty() {
        return !!this.value;
    }
    setDescribedByIds(ids) {
        // noop
    }
    setAriaLabel(value) {
        // noop
    }
    get elementRef() {
        return this.timefield.elementRef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: NxTimefieldControl, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.2", type: NxTimefieldControl, isStandalone: true, selector: "nx-timefield-control", inputs: { value: "value", focused: "focused", required: "required", disabled: "disabled", readonly: "readonly", errorState: "errorState", placeholder: "placeholder", updateOn: "updateOn" }, providers: [{ provide: NxFormfieldControl, useExisting: NxTimefieldControl }], ngImport: i0, template: ``, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: NxTimefieldControl, decorators: [{
            type: Component,
            args: [{
                    selector: 'nx-timefield-control',
                    template: ``,
                    standalone: true,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    providers: [{ provide: NxFormfieldControl, useExisting: NxTimefieldControl }],
                }]
        }], propDecorators: { value: [{
                type: Input
            }], focused: [{
                type: Input
            }], required: [{
                type: Input
            }], disabled: [{
                type: Input
            }], readonly: [{
                type: Input
            }], errorState: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], updateOn: [{
                type: Input
            }] } });
class NxTimefieldComponent {
    /* Whether to show the timepicker. Not enabled by default. */
    set withTimepicker(value) {
        this.focusMonitor.stopMonitoring(this.toggleButton?.nativeElement);
        this._withTimepicker = value;
        setTimeout(() => this.toggleButton && this.focusMonitor.monitor(this.toggleButton.nativeElement));
    }
    get withTimepicker() {
        return this._withTimepicker;
    }
    handleKeyDown(event) {
        if (!this.withTimepicker) {
            return;
        }
        if (!this.isOpen && event.target instanceof HTMLButtonElement && (event.key === 'Enter' || event.key === ' ')) {
            // space and enter on the button are handled by the click handler
            // we return here otherwise the overlay would get closed immediately
            return;
        }
        if (this.isOpen) {
            switch (event.key) {
                case 'Enter':
                case ' ':
                    event.preventDefault();
                    this.selectOption(this._keyManager?.activeItem?.value);
                    break;
                default:
                    this._keyManager?.onKeydown(event);
                    break;
            }
        }
        else if (!this.isOpen && event.key !== 'Enter' && event.key !== 'Tab' && event.key !== 'Shift') {
            this.toggleOverlay();
        }
    }
    /** The id of the minutes input field. */
    get idMinutes() {
        return this._idMinutes;
    }
    /** The id of the hours input field. */
    get idHours() {
        return this._idHours;
    }
    /** The id of the am/pm selection. */
    get idRadioGroup() {
        return this._idRadioGroup;
    }
    /** The id of option list in the timepicker overlay. */
    get idOptionList() {
        return this._idOptionList;
    }
    /** @docs-private */
    get maxHours() {
        return this._maxHours;
    }
    /** @docs-private */
    get minHours() {
        return this._minHours;
    }
    /** @docs-private */
    get maxMinutes() {
        return this._maxMinutes;
    }
    /** @docs-private */
    get minMinutes() {
        return this._minMinutes;
    }
    /** @docs-private */
    set time(value) {
        this._time = value;
        this._onChangeCallback(value);
        this._cdr.markForCheck();
    }
    get time() {
        return this._time;
    }
    /** Whether to show the time in 12-hour format with AM/PM toggle. Default: false. */
    set twelveHourFormat(value) {
        this._twelveHourFormat = coerceBooleanProperty(value);
        if (this._twelveHourFormat) {
            this._maxHours = 12;
            this._minHours = 1;
            this._toggleAMPM = 'AM';
        }
        else {
            this._maxHours = 23;
            this._minHours = 0;
            this._toggleAMPM = null;
        }
        this._cdr.markForCheck();
    }
    get twelveHourFormat() {
        return this._twelveHourFormat;
    }
    /** Sets the label which is displayed on top of timefield. */
    set label(value) {
        if (this._label !== value) {
            this._label = value;
            this._cdr.markForCheck();
        }
    }
    get label() {
        return this._label;
    }
    /** Sets the AM radio button label which is displayed in radio group. */
    set labelAM(value) {
        if (this._labelAM !== value) {
            this._labelAM = value;
            this._cdr.markForCheck();
        }
    }
    get labelAM() {
        return this._labelAM;
    }
    /** Sets the PM radio button label which is displayed in radio group. */
    set labelPM(value) {
        if (this._labelPM !== value) {
            this._labelPM = value;
            this._cdr.markForCheck();
        }
    }
    get labelPM() {
        return this._labelPM;
    }
    /** Sets the placeholder of hours field. Default: 'hh' */
    set placeholderHours(value) {
        if (this._placeholderHours !== value) {
            this._placeholderHours = value;
            this._cdr.markForCheck();
        }
    }
    get placeholderHours() {
        return this._placeholderHours;
    }
    /** Sets the placeholder of minutes field. Default: 'mm' */
    set placeholderMinutes(value) {
        if (this._placeholderMinutes !== value) {
            this._placeholderMinutes = value;
            this._cdr.markForCheck();
        }
    }
    get placeholderMinutes() {
        return this._placeholderMinutes;
    }
    /** Whether the timefield is required. */
    set required(value) {
        this._required = coerceBooleanProperty(value);
    }
    get required() {
        return this._required;
    }
    /** Whether the timefield uses the negative set of styling. */
    set negative(value) {
        const newValue = coerceBooleanProperty(value);
        if (this._negative !== newValue) {
            this._negative = newValue;
            this._cdr.markForCheck();
        }
    }
    get negative() {
        return this._negative;
    }
    /** Whether the timefield is disabled. */
    set disabled(value) {
        const newValue = coerceBooleanProperty(value);
        if (this._disabled !== newValue) {
            this._disabled = newValue;
            this._cdr.markForCheck();
        }
    }
    get disabled() {
        return this._disabled;
    }
    /** @docs-private */
    set hours(value) {
        this._hours = value;
        this._cdr.markForCheck();
    }
    get hours() {
        return this._hours;
    }
    /** @docs-private */
    set minutes(value) {
        this._minutes = value;
        this._cdr.markForCheck();
    }
    get minutes() {
        return this._minutes;
    }
    get elementRef() {
        return this._elementRef;
    }
    constructor(_cdr, 
    /** @docs-private */ ngControl, _errorStateMatcher, _parentForm, _parentFormGroup, _intl, _elementRef, 
    // TODO use these default options
    _formfieldDefaultOptions, _timefieldDefaultOptions) {
        this._cdr = _cdr;
        this.ngControl = ngControl;
        this._errorStateMatcher = _errorStateMatcher;
        this._parentForm = _parentForm;
        this._parentFormGroup = _parentFormGroup;
        this._intl = _intl;
        this._elementRef = _elementRef;
        this._formfieldDefaultOptions = _formfieldDefaultOptions;
        this._timefieldDefaultOptions = _timefieldDefaultOptions;
        /** @docs-private */
        this.errorState = false;
        this.isOpen = false;
        this.intl = inject(NxTimefieldIntl);
        this.focusMonitor = inject(FocusMonitor);
        /* The appearance of the formfield. Should be mostly handled via dependency injection and not over this input. */
        this.appearance = this._formfieldDefaultOptions?.appearance ?? 'auto';
        /* The hint to be shown below the field. */
        this.hint = '';
        /* The optional label for the formfield. */
        this.optionalLabel = '';
        this._withTimepicker = false;
        /* Event that emits the time in 24h ISO format. */
        this.valueChange = new EventEmitter();
        this.destroyRef = inject(DestroyRef);
        this.timeList = [];
        this._idMinutes = `nx-timefield__minutes-${nextId++}`;
        this._idHours = `nx-timefield__hours-${nextId++}`;
        this._idRadioGroup = `nx-timefield__radio-group-${nextId++}`;
        this._idOptionList = `nx-timefield__list-${nextId++}`;
        this._maxHours = 23;
        this._minHours = 0;
        this._maxMinutes = 59;
        this._minMinutes = 0;
        this._twelveHourFormat = false;
        this._labelAM = 'AM';
        this._labelPM = 'PM';
        this._placeholderHours = 'hh';
        this._placeholderMinutes = 'mm';
        this._negative = false;
        this.pickerStartTime = DEFAULT_START_TIME;
        this.pickerEndTime = DEFAULT_END_TIME;
        this.pickerTimeInterval = DEFAULT_TIME_SPAN;
        this._disabled = false;
        this.hasFocus = false;
        this._overlayWidth = '';
        this._onTouchedCallback = () => { };
        this._onChangeCallback = () => { };
        if (_timefieldDefaultOptions) {
            this.withTimepicker = this._timefieldDefaultOptions?.withTimepicker ?? false;
        }
        if (this.ngControl) {
            // Note: we provide the value accessor through here, instead of
            // the `providers` to avoid running into a circular import.
            this.ngControl.valueAccessor = this;
        }
    }
    ngAfterViewInit() {
        // this.focusMonitor.monitor(this.toggleButton?.nativeElement);
        this._keyManager = new ActiveDescendantKeyManager(this.timepickerOptions).withWrap();
        this._keyManager.change.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            if (this._keyManager?.activeItem) {
                this.scrollOptionIntoView(this._keyManager?.activeItem);
            }
        });
    }
    ngDoCheck() {
        if (this.ngControl) {
            // We need to re-evaluate this on every change detection cycle, because there are some
            // error triggers that we can't subscribe to (e.g. parent form submissions). This means
            // that whatever logic is in here has to be super lean or we risk destroying the performance.
            this.updateErrorState();
        }
    }
    ngOnInit() {
        this._createTimelist();
    }
    _createTimelist() {
        this.timeList = getTimeArray(this.pickerStartTime, this.pickerEndTime, this.pickerTimeInterval, this.twelveHourFormat);
    }
    ngOnChanges(changes) {
        if (changes.time || changes.pickerStartTime || changes.pickerEndTime || changes.pickerTimeInterval || changes.twelveHourFormat) {
            this._createTimelist();
            setTimeout(() => this.scrollSelectedItemIntoView());
        }
    }
    ngOnDestroy() {
        this.focusMonitor.stopMonitoring(this.toggleButton?.nativeElement);
    }
    /** @docs-private */
    updateErrorState() {
        const oldState = this.errorState;
        const parent = this._parentFormGroup || this._parentForm;
        const control = this.ngControl ? this.ngControl.control : null;
        const newState = this._errorStateMatcher.isErrorState(control, parent);
        if (newState !== oldState) {
            this.errorState = newState;
        }
    }
    toggleOverlay() {
        this.isOpen ? this.closeOverlay() : this.openOverlay();
    }
    openOverlay() {
        if (this.disabled) {
            return;
        }
        this._overlayWidth = this.overlayOrigin.elementRef.nativeElement.getBoundingClientRect().width;
        this.isOpen = true;
        setTimeout(() => this.scrollSelectedItemIntoView());
    }
    closeOverlay() {
        this.isOpen = false;
        this._onTouchedCallback();
    }
    toggleButtonClick() {
        this.toggleOverlay();
    }
    _convertToISOFormat(hours, minutes) {
        return `${hours}:${minutes}`;
    }
    _updateTime() {
        this._time = null;
        if (this._isValidInput(this.hours) && this._isValidInput(this.minutes)) {
            const hours = Number(this.hours);
            const minutes = Number(this.minutes);
            if (this._isValidTime(hours, 'hours') && this._isValidTime(minutes, 'minutes')) {
                this._time = this._timeInTwentyFourHourFormat(hours, minutes);
            }
        }
        this._onChangeCallback(this._time);
    }
    _onFocus(event) {
        this.hasFocus = true;
        if (event.target instanceof HTMLInputElement) {
            event.target.select();
        }
    }
    _getAriaLabel(type) {
        let label;
        switch (type) {
            case 'hours':
                label = this._intl.inputFieldHoursAriaLabel;
                break;
            case 'minutes':
                label = this._intl.inputFieldMinutesAriaLabel;
                break;
        }
        return label;
    }
    _onInput(event, type) {
        const target = event.target;
        if (type === 'hours') {
            this.hours = target.value;
            if (target.value.length === 2 && target.selectionStart === 2) {
                this.inputMinutes.nativeElement.focus();
            }
            // user has entered the first digit again. for quicker typing select the second digit
            if (target.value.length === 2 && target.selectionStart === 1) {
                target.selectionEnd = 2;
            }
        }
        else if (type === 'minutes') {
            this.minutes = target.value;
        }
        this._updateTime();
        const closestOption = this.findClosestOption(this.time);
        if (closestOption) {
            this._keyManager?.setActiveItem(closestOption);
        }
    }
    _onBlur(event) {
        if (!this.elementRef.nativeElement.contains(event.relatedTarget) &&
            !this.overlay?.overlayRef?.overlayElement?.contains(event.relatedTarget)) {
            this._onTouchedCallback();
            this.hasFocus = false;
            this.closeOverlay();
        }
    }
    _onInputBlur(element) {
        // set 0X is the value entered in X
        if (element === 'hours' && Number(this.hours) < 10 && this.hours !== '') {
            this.hours = pad(String(this.hours));
        }
        else if (element === 'minutes' && Number(this.minutes) < 10 && this.minutes !== '') {
            this.minutes = pad(String(this.minutes));
        }
    }
    _timeInTwentyFourHourFormat(hours, minutes) {
        if (this.twelveHourFormat) {
            // 12h to 24h conversion
            if (this._toggleAMPM === 'AM') {
                if (hours === 12) {
                    hours -= 12;
                }
            }
            else if (this._toggleAMPM === 'PM') {
                if (hours >= 1 && hours < 12) {
                    hours += 12;
                }
            }
        }
        return this._convertToISOFormat(pad(String(hours)), pad(String(minutes)));
    }
    _isValidInput(value) {
        const numExp = /^\d{1,2}$/;
        return numExp.test(value);
    }
    _isValidTime(value, type) {
        let valid = false;
        const numVal = Number(value);
        if (type === 'minutes') {
            valid = !!(numVal >= this._minMinutes && numVal <= this._maxMinutes);
        }
        else if (type === 'hours') {
            valid = !!(numVal >= this._minHours && numVal <= this._maxHours);
        }
        return valid;
    }
    _parseAndSetTime(value) {
        const valueInHoursAndMinutes = value.split(':');
        if (valueInHoursAndMinutes &&
            valueInHoursAndMinutes.length === 2 &&
            this._isValidInput(valueInHoursAndMinutes[0]) &&
            this._isValidInput(valueInHoursAndMinutes[1])) {
            let hours = Number(valueInHoursAndMinutes[0]);
            const minutes = Number(valueInHoursAndMinutes[1]);
            if (this.twelveHourFormat) {
                // 24h to 12h conversion
                if (hours === 0) {
                    hours += 12;
                    this._toggleAMPM = 'AM';
                }
                else if (hours > 12 && hours <= 23) {
                    hours -= 12;
                    this._toggleAMPM = 'PM';
                }
                else if (hours === 12) {
                    this._toggleAMPM = 'PM';
                }
            }
            if (this._isValidTime(hours, 'hours') && this._isValidTime(minutes, 'minutes')) {
                this.hours = pad(String(hours));
                this.minutes = pad(String(minutes));
                // model value should always be in 24h format
                return this._timeInTwentyFourHourFormat(hours, minutes);
            }
        }
        return null;
    }
    selectOption(value) {
        if (!value) {
            return;
        }
        const [hours] = value.split(':');
        this._toggleAMPM = Number(hours) >= 12 ? 'PM' : 'AM';
        this.writeValue(value);
        this.closeOverlay();
    }
    preventFocus(event) {
        event.preventDefault();
    }
    scrollSelectedItemIntoView() {
        const selectedItem = this.findClosestOption(this.time);
        if (selectedItem) {
            this._keyManager?.setActiveItem(selectedItem);
            this.scrollOptionIntoView(selectedItem);
        }
    }
    findClosestOption(time) {
        const closestTime = getClosestTime(this.timeList.map(t => t.value), time || '00:00');
        const option = this.timepickerOptions?.find(option => option.value === closestTime);
        return option;
    }
    scrollOptionIntoView(option) {
        option.element.scrollIntoView({ block: 'center' });
    }
    writeValue(value) {
        this._hours = '';
        this._minutes = '';
        this._time = null;
        if (value) {
            this.time = this._parseAndSetTime(value);
        }
        this.valueChange.emit(this.time);
    }
    registerOnChange(fn) {
        this._onChangeCallback = fn;
    }
    registerOnTouched(fn) {
        this._onTouchedCallback = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: NxTimefieldComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i1.NgControl, optional: true, self: true }, { token: i2.ErrorStateMatcher }, { token: i1.NgForm, optional: true }, { token: i1.FormGroupDirective, optional: true }, { token: NxTimefieldIntl }, { token: i0.ElementRef }, { token: FORMFIELD_DEFAULT_OPTIONS, optional: true }, { token: TIMEFIELD_DEFAULT_OPTIONS, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "17.3.2", type: NxTimefieldComponent, selector: "nx-timefield", inputs: { appearance: "appearance", hint: "hint", optionalLabel: "optionalLabel", withTimepicker: ["withTimepicker", "withTimepicker", booleanAttribute], twelveHourFormat: "twelveHourFormat", label: "label", labelAM: "labelAM", labelPM: "labelPM", placeholderHours: "placeholderHours", placeholderMinutes: "placeholderMinutes", required: "required", negative: "negative", pickerStartTime: "pickerStartTime", pickerEndTime: "pickerEndTime", pickerTimeInterval: ["pickerTimeInterval", "pickerTimeInterval", numberAttribute], disabled: "disabled" }, outputs: { valueChange: "valueChange" }, host: { listeners: { "focusout": "_onBlur($event)" }, properties: { "class.has-error": "errorState", "class.is-negative": "negative", "class.is-disabled": "disabled", "class.has-timepicker": "withTimepicker" } }, viewQueries: [{ propertyName: "timePickerList", first: true, predicate: ["list"], descendants: true }, { propertyName: "toggleButton", first: true, predicate: ["toggleButton"], descendants: true }, { propertyName: "overlayOrigin", first: true, predicate: ["overlayOrigin"], descendants: true }, { propertyName: "inputMinutes", first: true, predicate: ["inputMinutes"], descendants: true }, { propertyName: "inputHours", first: true, predicate: ["inputHours"], descendants: true }, { propertyName: "overlay", first: true, predicate: CdkConnectedOverlay, descendants: true }, { propertyName: "timepickerOptions", predicate: NxTimefieldOption, descendants: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"nx-timefield__wrapper\" [ngClass]=\"{ 'has-outline': appearance === 'outline' }\">\n    <nx-formfield\n        cdkOverlayOrigin\n        #overlayOrigin=\"cdkOverlayOrigin\"\n        [label]=\"label\"\n        [optionalLabel]=\"optionalLabel\"\n        [appearance]=\"appearance\"\n        floatLabel=\"always\"\n        [negative]=\"negative ? 'negative' : ''\"\n    >\n        <input\n            [id]=\"idHours\"\n            #inputHours\n            [value]=\"hours || null\"\n            class=\"nx-timefield-input__field__hours\"\n            type=\"text\"\n            maxlength=\"2\"\n            autocomplete=\"off\"\n            role=\"combobox\"\n            (input)=\"_onInput($event, 'hours')\"\n            (keydown)=\"handleKeyDown($event)\"\n            (focus)=\"_onFocus($event)\"\n            (blur)=\"_onInputBlur('hours')\"\n            [required]=\"required\"\n            [disabled]=\"disabled\"\n            [placeholder]=\"placeholderHours\"\n            aria-autocomplete=\"list\"\n            [attr.aria-required]=\"required\"\n            [attr.aria-label]=\"_getAriaLabel('hours')\"\n            [attr.aria-expanded]=\"isOpen.toString()\"\n            [attr.aria-controls]=\"isOpen ? idOptionList : null\"\n            [attr.aria-activedescendant]=\"_keyManager?.activeItem?.id\"\n        />\n        <span class=\"nx-timefield-hours-separator\">:</span>\n        <input\n            [id]=\"idMinutes\"\n            #inputMinutes\n            [value]=\"minutes || null\"\n            class=\"nx-timefield-input__field__minutes\"\n            type=\"text\"\n            maxlength=\"2\"\n            autocomplete=\"off\"\n            role=\"combobox\"\n            (input)=\"_onInput($event, 'minutes')\"\n            (focus)=\"_onFocus($event)\"\n            (blur)=\"_onInputBlur('minutes')\"\n            (keydown)=\"handleKeyDown($event)\"\n            [required]=\"required\"\n            [disabled]=\"disabled\"\n            [placeholder]=\"placeholderMinutes\"\n            aria-autocomplete=\"list\"\n            [attr.aria-required]=\"required\"\n            [attr.aria-label]=\"_getAriaLabel('minutes')\"\n            [attr.aria-expanded]=\"isOpen.toString()\"\n            [attr.aria-controls]=\"isOpen ? idOptionList : null\"\n            [attr.aria-activedescendant]=\"_keyManager?.activeItem?.id\"\n        />\n        @if (withTimepicker) {\n            <button\n                nxFormfieldSuffix\n                #toggleButton\n                [ngClass]=\"{ 'nx-timepicker-toggle--disabled': disabled }\"\n                (click)=\"!disabled && toggleButtonClick()\"\n                (focus)=\"_onFocus($event)\"\n                class=\"nx-timepicker-toggle-button\"\n                type=\"button\"\n                [disabled]=\"disabled\"\n                [attr.aria-label]=\"intl.buttonOpenTimepickerAriaLabel\"\n                [attr.aria-expanded]=\"isOpen.toString()\"\n                [attr.aria-controls]=\"isOpen ? idOptionList : null\"\n                [attr.aria-activedescendant]=\"_keyManager?.activeItem?.id\"\n                (keydown)=\"handleKeyDown($event)\"\n            >\n                <nx-icon name=\"clock-o\" aria-hidden=\"true\" size=\"s\"></nx-icon>\n            </button>\n        }\n        <nx-timefield-control style=\"display: none\" [errorState]=\"errorState\" [disabled]=\"disabled\" [required]=\"required\" [value]=\"time\" [focused]=\"hasFocus\" />\n        <span nxFormfieldHint *ngIf=\"hint\"> {{ hint }} </span>\n        <ng-content select=\"[nxFormfieldError]\" ngProjectAs=\"[nxFormfieldError]\"></ng-content>\n    </nx-formfield>\n\n    <nx-radio-toggle\n        [name]=\"idRadioGroup\"\n        [(ngModel)]=\"_toggleAMPM\"\n        (ngModelChange)=\"_updateTime()\"\n        *ngIf=\"twelveHourFormat\"\n        (click)=\"_updateTime()\"\n        [disabled]=\"disabled\"\n        [variant]=\"negative ? 'small negative' : 'small'\"\n        disableMobile\n    >\n        <nx-radio-toggle-button disableMobile value=\"AM\">{{ labelAM }}</nx-radio-toggle-button>\n        <nx-radio-toggle-button disableMobile value=\"PM\">{{ labelPM }}</nx-radio-toggle-button>\n    </nx-radio-toggle>\n</div>\n\n<ng-template\n    cdkConnectedOverlay\n    [cdkConnectedOverlayOpen]=\"isOpen\"\n    [cdkConnectedOverlayHasBackdrop]=\"true\"\n    [cdkConnectedOverlayOrigin]=\"overlayOrigin\"\n    [cdkConnectedOverlayMinWidth]=\"_overlayWidth\"\n    [cdkConnectedOverlayBackdropClass]=\"'cdk-overlay-transparent-backdrop'\"\n    (backdropClick)=\"closeOverlay()\"\n    (detach)=\"closeOverlay()\"\n>\n    <section\n        role=\"dialog\"\n        aria-modal=\"true\"\n        [ngClass]=\"{\n            'has-outline': appearance === 'outline',\n            'twelve-hour-format': twelveHourFormat\n        }\"\n    >\n        <ul\n            #list\n            role=\"listbox\"\n            [id]=\"idOptionList\"\n            tabindex=\"0\"\n            class=\"option-list\"\n            [attr.aria-activedescendant]=\"_keyManager?.activeItem?.id\"\n            (keydown)=\"handleKeyDown($event)\"\n        >\n            @for (item of timeList; track item.value; let i = $index) {\n                <li [nxTimefieldOption]=\"item.value\" (mousedown)=\"preventFocus($event)\" (click)=\"selectOption(item.value)\">\n                    <div class=\"option-label\">{{ item.label }}</div>\n                </li>\n            }\n        </ul>\n    </section>\n</ng-template>\n", styles: [":host{display:inline-block}.nx-timefield__wrapper{display:flex;justify-content:space-between}.nx-timefield__wrapper:focus{outline:none}.nx-timefield-input__field__hours,.nx-timefield-input__field__minutes,.nx-timefield-hours-separator{text-align:inherit;font-size:inherit;font-weight:inherit;color:inherit;text-align:left;border:0;outline:0;border-radius:0;padding:0;margin:0;background:transparent;box-sizing:border-box}.nx-timefield-input__field__hours,.nx-timefield-input__field__minutes{width:2ch;padding:0;margin:0}.nx-timefield-input__field__minutes{width:32px}:host-context(.has-timepicker) .nx-timefield-input__field__minutes{margin-right:18px}.nx-timefield-hours-separator{margin:0 4px}nx-timepicker{display:block;height:24px;align-self:center}nx-radio-toggle{margin-left:24px;height:max-content;margin-top:var(--formfield-label-height)}::ng-deep .nx-formfield__wrapper{padding-bottom:2px}::ng-deep .nx-formfield__input-container:focus-within{border-color:var(--timefield-active-color)}.has-outline nx-radio-toggle{padding-top:4px;margin-bottom:4px;margin-left:16px;margin-top:var(--formfield-outline-label-height)}.has-outline ::ng-deep .nx-timefield-input__field__hours,.has-outline ::ng-deep .nx-timefield-input__field__minutes{margin:0;font-weight:400;line-height:24px;padding:0}.has-outline ::ng-deep .nx-timefield-input__field__hours{width:18px}.has-outline ::ng-deep .nx-timefield-input__field__minutes{max-width:58px}.has-outline ::ng-deep .nx-timefield-hours-separator{margin:0 2px;align-self:center}.has-outline ::ng-deep .nx-formfield__input{padding:2px 8px}:host:focus{outline:none}:host.is-negative .nx-timefield__label,:host.is-negative .nx-timefield-input__field__hours,:host.is-negative .nx-timefield-input__field__minutes,:host.is-negative .nx-timefield-hours-separator,:host.is-negative .nx-timefield-input__container{color:var(--negative)}:host.is-negative ::ng-deep .nx-formfield__input-container:focus-within{border-color:var(--negative)!important}:host.is-disabled.is-negative{cursor:not-allowed}:host.is-disabled.is-negative .nx-timefield__label,:host.is-disabled.is-negative .nx-timefield-input__field__hours,:host.is-disabled.is-negative .nx-timefield-input__field__minutes,:host.is-disabled.is-negative .nx-timefield-hours-separator,:host.is-disabled.is-negative .nx-timefield-input__container{cursor:not-allowed;color:var(--timefield-disabled-color)}:host.is-disabled.is-negative .nx-timefield-input__container{box-shadow:inset 0 -1px 0 0 var(--timefield-disabled-color)}:host(.is-negative) .nx-timepicker-toggle-button{color:var(--negative)}:host(.is-negative) :host(.has-outline) .nx-timepicker-toggle-button{color:inherit}.nx-timepicker-toggle-button{border:none;background-color:transparent;color:inherit;outline:none;cursor:pointer;padding:0;margin:0;display:flex}.nx-timepicker-toggle-button:hover{color:var(--hover-primary)}.nx-timepicker-toggle-button.nx-timepicker-toggle--disabled,.nx-timepicker-toggle-button.nx-timepicker-toggle--disabled:hover{cursor:not-allowed;color:var(--timefield-disabled-color)}.nx-timepicker-toggle-button::-moz-focus-inner{border:0}.nx-timepicker-toggle-button:active{outline:none}@media screen and (forced-colors: active){.nx-timepicker-toggle-button{color:ButtonText}}.nx-timepicker-toggle-button.cdk-keyboard-focused{border-radius:4px;box-shadow:var(--focus-box-shadow)}@media screen and (forced-colors: active),(forced-colors: active){.nx-timepicker-toggle-button.cdk-keyboard-focused{box-shadow:0 0 0 2px background,0 0 0 6px CanvasText;outline:4px solid CanvasText;outline-offset:2px}}section{width:100%}.option-list{outline:none;height:252px;background:var(--timepicker-layout-background-color);text-align:center;padding:8px 0;margin:6px 0;overflow-y:scroll;color:var(--timepicker-label-color);font-weight:400;box-shadow:0 2px 4px #41414180;border-radius:4px;width:100%}.option-list>li{list-style-type:none;width:100%;align-content:center;font-size:20px;line-height:28px;outline:none;padding:8px 32px}.option-list>li:hover{color:var(--timepicker-label-hover-color);cursor:pointer}.option-list>li.is-active .option-label{color:var(--timefield-active-color);border-radius:4px;box-shadow:var(--focus-box-shadow)}.has-outline .option-list>li{font-size:16px;line-height:24px}.twelve-hour-format .option-list>li{text-align:end}.option-list>li:last-child{border-bottom:none}\n"], dependencies: [{ kind: "directive", type: i4.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i4.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: i5.NxIconComponent, selector: "nx-icon", inputs: ["name", "outline", "fill", "size", "font"] }, { kind: "directive", type: i6.CdkConnectedOverlay, selector: "[cdk-connected-overlay], [connected-overlay], [cdkConnectedOverlay]", inputs: ["cdkConnectedOverlayOrigin", "cdkConnectedOverlayPositions", "cdkConnectedOverlayPositionStrategy", "cdkConnectedOverlayOffsetX", "cdkConnectedOverlayOffsetY", "cdkConnectedOverlayWidth", "cdkConnectedOverlayHeight", "cdkConnectedOverlayMinWidth", "cdkConnectedOverlayMinHeight", "cdkConnectedOverlayBackdropClass", "cdkConnectedOverlayPanelClass", "cdkConnectedOverlayViewportMargin", "cdkConnectedOverlayScrollStrategy", "cdkConnectedOverlayOpen", "cdkConnectedOverlayDisableClose", "cdkConnectedOverlayTransformOriginOn", "cdkConnectedOverlayHasBackdrop", "cdkConnectedOverlayLockPosition", "cdkConnectedOverlayFlexibleDimensions", "cdkConnectedOverlayGrowAfterOpen", "cdkConnectedOverlayPush", "cdkConnectedOverlayDisposeOnNavigation"], outputs: ["backdropClick", "positionChange", "attach", "detach", "overlayKeydown", "overlayOutsideClick"], exportAs: ["cdkConnectedOverlay"] }, { kind: "directive", type: i6.CdkOverlayOrigin, selector: "[cdk-overlay-origin], [overlay-origin], [cdkOverlayOrigin]", exportAs: ["cdkOverlayOrigin"] }, { kind: "component", type: i7.NxRadioToggleComponent, selector: "nx-radio-toggle", inputs: ["disableMobile", "disabled", "name", "variant", "selection"] }, { kind: "component", type: i7.NxRadioToggleButtonComponent, selector: "nx-radio-toggle-button", inputs: ["disableMobile", "disabled", "ariaLabel", "selected"] }, { kind: "component", type: i8.NxFormfieldComponent, selector: "nx-formfield", inputs: ["label", "optionalLabel", "floatLabel", "negative", "appearance", "updateOn"] }, { kind: "directive", type: i8.NxFormfieldHintDirective, selector: "[nxFormfieldHint]", inputs: ["id"] }, { kind: "directive", type: i8.NxFormfieldSuffixDirective, selector: "[nxFormfieldSuffix]" }, { kind: "directive", type: NxTimefieldOption, selector: "[nxTimefieldOption]", inputs: ["nxTimefieldOption", "selected", "id"], exportAs: ["nxTimefieldOption"] }, { kind: "component", type: NxTimefieldControl, selector: "nx-timefield-control", inputs: ["value", "focused", "required", "disabled", "readonly", "errorState", "placeholder", "updateOn"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: NxTimefieldComponent, decorators: [{
            type: Component,
            args: [{ selector: 'nx-timefield', changeDetection: ChangeDetectionStrategy.OnPush, host: {
                        '[class.has-error]': 'errorState',
                        '[class.is-negative]': 'negative',
                        '[class.is-disabled]': 'disabled',
                        '[class.has-timepicker]': 'withTimepicker',
                        '(focusout)': '_onBlur($event)',
                    }, template: "<div class=\"nx-timefield__wrapper\" [ngClass]=\"{ 'has-outline': appearance === 'outline' }\">\n    <nx-formfield\n        cdkOverlayOrigin\n        #overlayOrigin=\"cdkOverlayOrigin\"\n        [label]=\"label\"\n        [optionalLabel]=\"optionalLabel\"\n        [appearance]=\"appearance\"\n        floatLabel=\"always\"\n        [negative]=\"negative ? 'negative' : ''\"\n    >\n        <input\n            [id]=\"idHours\"\n            #inputHours\n            [value]=\"hours || null\"\n            class=\"nx-timefield-input__field__hours\"\n            type=\"text\"\n            maxlength=\"2\"\n            autocomplete=\"off\"\n            role=\"combobox\"\n            (input)=\"_onInput($event, 'hours')\"\n            (keydown)=\"handleKeyDown($event)\"\n            (focus)=\"_onFocus($event)\"\n            (blur)=\"_onInputBlur('hours')\"\n            [required]=\"required\"\n            [disabled]=\"disabled\"\n            [placeholder]=\"placeholderHours\"\n            aria-autocomplete=\"list\"\n            [attr.aria-required]=\"required\"\n            [attr.aria-label]=\"_getAriaLabel('hours')\"\n            [attr.aria-expanded]=\"isOpen.toString()\"\n            [attr.aria-controls]=\"isOpen ? idOptionList : null\"\n            [attr.aria-activedescendant]=\"_keyManager?.activeItem?.id\"\n        />\n        <span class=\"nx-timefield-hours-separator\">:</span>\n        <input\n            [id]=\"idMinutes\"\n            #inputMinutes\n            [value]=\"minutes || null\"\n            class=\"nx-timefield-input__field__minutes\"\n            type=\"text\"\n            maxlength=\"2\"\n            autocomplete=\"off\"\n            role=\"combobox\"\n            (input)=\"_onInput($event, 'minutes')\"\n            (focus)=\"_onFocus($event)\"\n            (blur)=\"_onInputBlur('minutes')\"\n            (keydown)=\"handleKeyDown($event)\"\n            [required]=\"required\"\n            [disabled]=\"disabled\"\n            [placeholder]=\"placeholderMinutes\"\n            aria-autocomplete=\"list\"\n            [attr.aria-required]=\"required\"\n            [attr.aria-label]=\"_getAriaLabel('minutes')\"\n            [attr.aria-expanded]=\"isOpen.toString()\"\n            [attr.aria-controls]=\"isOpen ? idOptionList : null\"\n            [attr.aria-activedescendant]=\"_keyManager?.activeItem?.id\"\n        />\n        @if (withTimepicker) {\n            <button\n                nxFormfieldSuffix\n                #toggleButton\n                [ngClass]=\"{ 'nx-timepicker-toggle--disabled': disabled }\"\n                (click)=\"!disabled && toggleButtonClick()\"\n                (focus)=\"_onFocus($event)\"\n                class=\"nx-timepicker-toggle-button\"\n                type=\"button\"\n                [disabled]=\"disabled\"\n                [attr.aria-label]=\"intl.buttonOpenTimepickerAriaLabel\"\n                [attr.aria-expanded]=\"isOpen.toString()\"\n                [attr.aria-controls]=\"isOpen ? idOptionList : null\"\n                [attr.aria-activedescendant]=\"_keyManager?.activeItem?.id\"\n                (keydown)=\"handleKeyDown($event)\"\n            >\n                <nx-icon name=\"clock-o\" aria-hidden=\"true\" size=\"s\"></nx-icon>\n            </button>\n        }\n        <nx-timefield-control style=\"display: none\" [errorState]=\"errorState\" [disabled]=\"disabled\" [required]=\"required\" [value]=\"time\" [focused]=\"hasFocus\" />\n        <span nxFormfieldHint *ngIf=\"hint\"> {{ hint }} </span>\n        <ng-content select=\"[nxFormfieldError]\" ngProjectAs=\"[nxFormfieldError]\"></ng-content>\n    </nx-formfield>\n\n    <nx-radio-toggle\n        [name]=\"idRadioGroup\"\n        [(ngModel)]=\"_toggleAMPM\"\n        (ngModelChange)=\"_updateTime()\"\n        *ngIf=\"twelveHourFormat\"\n        (click)=\"_updateTime()\"\n        [disabled]=\"disabled\"\n        [variant]=\"negative ? 'small negative' : 'small'\"\n        disableMobile\n    >\n        <nx-radio-toggle-button disableMobile value=\"AM\">{{ labelAM }}</nx-radio-toggle-button>\n        <nx-radio-toggle-button disableMobile value=\"PM\">{{ labelPM }}</nx-radio-toggle-button>\n    </nx-radio-toggle>\n</div>\n\n<ng-template\n    cdkConnectedOverlay\n    [cdkConnectedOverlayOpen]=\"isOpen\"\n    [cdkConnectedOverlayHasBackdrop]=\"true\"\n    [cdkConnectedOverlayOrigin]=\"overlayOrigin\"\n    [cdkConnectedOverlayMinWidth]=\"_overlayWidth\"\n    [cdkConnectedOverlayBackdropClass]=\"'cdk-overlay-transparent-backdrop'\"\n    (backdropClick)=\"closeOverlay()\"\n    (detach)=\"closeOverlay()\"\n>\n    <section\n        role=\"dialog\"\n        aria-modal=\"true\"\n        [ngClass]=\"{\n            'has-outline': appearance === 'outline',\n            'twelve-hour-format': twelveHourFormat\n        }\"\n    >\n        <ul\n            #list\n            role=\"listbox\"\n            [id]=\"idOptionList\"\n            tabindex=\"0\"\n            class=\"option-list\"\n            [attr.aria-activedescendant]=\"_keyManager?.activeItem?.id\"\n            (keydown)=\"handleKeyDown($event)\"\n        >\n            @for (item of timeList; track item.value; let i = $index) {\n                <li [nxTimefieldOption]=\"item.value\" (mousedown)=\"preventFocus($event)\" (click)=\"selectOption(item.value)\">\n                    <div class=\"option-label\">{{ item.label }}</div>\n                </li>\n            }\n        </ul>\n    </section>\n</ng-template>\n", styles: [":host{display:inline-block}.nx-timefield__wrapper{display:flex;justify-content:space-between}.nx-timefield__wrapper:focus{outline:none}.nx-timefield-input__field__hours,.nx-timefield-input__field__minutes,.nx-timefield-hours-separator{text-align:inherit;font-size:inherit;font-weight:inherit;color:inherit;text-align:left;border:0;outline:0;border-radius:0;padding:0;margin:0;background:transparent;box-sizing:border-box}.nx-timefield-input__field__hours,.nx-timefield-input__field__minutes{width:2ch;padding:0;margin:0}.nx-timefield-input__field__minutes{width:32px}:host-context(.has-timepicker) .nx-timefield-input__field__minutes{margin-right:18px}.nx-timefield-hours-separator{margin:0 4px}nx-timepicker{display:block;height:24px;align-self:center}nx-radio-toggle{margin-left:24px;height:max-content;margin-top:var(--formfield-label-height)}::ng-deep .nx-formfield__wrapper{padding-bottom:2px}::ng-deep .nx-formfield__input-container:focus-within{border-color:var(--timefield-active-color)}.has-outline nx-radio-toggle{padding-top:4px;margin-bottom:4px;margin-left:16px;margin-top:var(--formfield-outline-label-height)}.has-outline ::ng-deep .nx-timefield-input__field__hours,.has-outline ::ng-deep .nx-timefield-input__field__minutes{margin:0;font-weight:400;line-height:24px;padding:0}.has-outline ::ng-deep .nx-timefield-input__field__hours{width:18px}.has-outline ::ng-deep .nx-timefield-input__field__minutes{max-width:58px}.has-outline ::ng-deep .nx-timefield-hours-separator{margin:0 2px;align-self:center}.has-outline ::ng-deep .nx-formfield__input{padding:2px 8px}:host:focus{outline:none}:host.is-negative .nx-timefield__label,:host.is-negative .nx-timefield-input__field__hours,:host.is-negative .nx-timefield-input__field__minutes,:host.is-negative .nx-timefield-hours-separator,:host.is-negative .nx-timefield-input__container{color:var(--negative)}:host.is-negative ::ng-deep .nx-formfield__input-container:focus-within{border-color:var(--negative)!important}:host.is-disabled.is-negative{cursor:not-allowed}:host.is-disabled.is-negative .nx-timefield__label,:host.is-disabled.is-negative .nx-timefield-input__field__hours,:host.is-disabled.is-negative .nx-timefield-input__field__minutes,:host.is-disabled.is-negative .nx-timefield-hours-separator,:host.is-disabled.is-negative .nx-timefield-input__container{cursor:not-allowed;color:var(--timefield-disabled-color)}:host.is-disabled.is-negative .nx-timefield-input__container{box-shadow:inset 0 -1px 0 0 var(--timefield-disabled-color)}:host(.is-negative) .nx-timepicker-toggle-button{color:var(--negative)}:host(.is-negative) :host(.has-outline) .nx-timepicker-toggle-button{color:inherit}.nx-timepicker-toggle-button{border:none;background-color:transparent;color:inherit;outline:none;cursor:pointer;padding:0;margin:0;display:flex}.nx-timepicker-toggle-button:hover{color:var(--hover-primary)}.nx-timepicker-toggle-button.nx-timepicker-toggle--disabled,.nx-timepicker-toggle-button.nx-timepicker-toggle--disabled:hover{cursor:not-allowed;color:var(--timefield-disabled-color)}.nx-timepicker-toggle-button::-moz-focus-inner{border:0}.nx-timepicker-toggle-button:active{outline:none}@media screen and (forced-colors: active){.nx-timepicker-toggle-button{color:ButtonText}}.nx-timepicker-toggle-button.cdk-keyboard-focused{border-radius:4px;box-shadow:var(--focus-box-shadow)}@media screen and (forced-colors: active),(forced-colors: active){.nx-timepicker-toggle-button.cdk-keyboard-focused{box-shadow:0 0 0 2px background,0 0 0 6px CanvasText;outline:4px solid CanvasText;outline-offset:2px}}section{width:100%}.option-list{outline:none;height:252px;background:var(--timepicker-layout-background-color);text-align:center;padding:8px 0;margin:6px 0;overflow-y:scroll;color:var(--timepicker-label-color);font-weight:400;box-shadow:0 2px 4px #41414180;border-radius:4px;width:100%}.option-list>li{list-style-type:none;width:100%;align-content:center;font-size:20px;line-height:28px;outline:none;padding:8px 32px}.option-list>li:hover{color:var(--timepicker-label-hover-color);cursor:pointer}.option-list>li.is-active .option-label{color:var(--timefield-active-color);border-radius:4px;box-shadow:var(--focus-box-shadow)}.has-outline .option-list>li{font-size:16px;line-height:24px}.twelve-hour-format .option-list>li{text-align:end}.option-list>li:last-child{border-bottom:none}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }, { type: i1.NgControl, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }] }, { type: i2.ErrorStateMatcher }, { type: i1.NgForm, decorators: [{
                    type: Optional
                }] }, { type: i1.FormGroupDirective, decorators: [{
                    type: Optional
                }] }, { type: NxTimefieldIntl }, { type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [FORMFIELD_DEFAULT_OPTIONS]
                }] }, { type: TimefieldDefaultOptions, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [TIMEFIELD_DEFAULT_OPTIONS]
                }] }], propDecorators: { appearance: [{
                type: Input
            }], hint: [{
                type: Input
            }], optionalLabel: [{
                type: Input
            }], withTimepicker: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], valueChange: [{
                type: Output
            }], timePickerList: [{
                type: ViewChild,
                args: ['list']
            }], toggleButton: [{
                type: ViewChild,
                args: ['toggleButton']
            }], overlayOrigin: [{
                type: ViewChild,
                args: ['overlayOrigin']
            }], inputMinutes: [{
                type: ViewChild,
                args: ['inputMinutes']
            }], inputHours: [{
                type: ViewChild,
                args: ['inputHours']
            }], overlay: [{
                type: ViewChild,
                args: [CdkConnectedOverlay]
            }], timepickerOptions: [{
                type: ViewChildren,
                args: [NxTimefieldOption]
            }], twelveHourFormat: [{
                type: Input
            }], label: [{
                type: Input
            }], labelAM: [{
                type: Input
            }], labelPM: [{
                type: Input
            }], placeholderHours: [{
                type: Input
            }], placeholderMinutes: [{
                type: Input
            }], required: [{
                type: Input
            }], negative: [{
                type: Input
            }], pickerStartTime: [{
                type: Input
            }], pickerEndTime: [{
                type: Input
            }], pickerTimeInterval: [{
                type: Input,
                args: [{ transform: numberAttribute }]
            }], disabled: [{
                type: Input
            }] } });

class NxTimefieldModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: NxTimefieldModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.2", ngImport: i0, type: NxTimefieldModule, declarations: [NxTimefieldComponent], imports: [CommonModule,
            FormsModule,
            NxRadioModule,
            NxIconModule,
            NxButtonModule,
            OverlayModule,
            NxRadioToggleModule,
            A11yModule,
            NxFormfieldModule,
            NxInputModule,
            ReactiveFormsModule,
            NxTimefieldOption,
            NxTimefieldControl], exports: [NxTimefieldComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: NxTimefieldModule, providers: [NxTimefieldIntl], imports: [CommonModule,
            FormsModule,
            NxRadioModule,
            NxIconModule,
            NxButtonModule,
            OverlayModule,
            NxRadioToggleModule,
            A11yModule,
            NxFormfieldModule,
            NxInputModule,
            ReactiveFormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: NxTimefieldModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        NxRadioModule,
                        NxIconModule,
                        NxButtonModule,
                        OverlayModule,
                        NxRadioToggleModule,
                        A11yModule,
                        NxFormfieldModule,
                        NxInputModule,
                        ReactiveFormsModule,
                        NxTimefieldOption,
                        NxTimefieldControl,
                    ],
                    declarations: [NxTimefieldComponent],
                    exports: [NxTimefieldComponent],
                    providers: [NxTimefieldIntl],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { DEFAULT_END_TIME, DEFAULT_START_TIME, DEFAULT_TIME_SPAN, NxTimefieldComponent, NxTimefieldControl, NxTimefieldIntl, NxTimefieldModule, NxTimefieldOption, TIMEFIELD_DEFAULT_OPTIONS, TIMEFIELD_DEFAULT_OPTIONS_FACTORY, TimefieldDefaultOptions };
